import React, { useState, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Expander from '@amzn/meridian/expander';
import { VictoryChart, VictoryAxis, VictoryLine, VictoryArea, VictoryStack, VictoryVoronoiContainer } from 'victory';
import { LegendProvider } from '@amzn/meridian/legend';
import victoryLinePlugin from '@amzn/meridian/legend/plugin/victory-line';
import victoryTooltipPlugin from '@amzn/meridian/legend/plugin/victory-tooltip';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';
import {
  CHART_WIDTH_RATIO,
  CHART_COLORS,
  buildTimeScale,
  transformChartData,
  buildIntervalLines,
  formatTooltipTitle,
  renderIntervalLines
} from './chartUtils';

const TOTAL_KEY = '__TOTAL__';
const NET_KEY = '__NET__';
const COHORT_LEGEND_VISIBLE_THRESHOLD = 5;

const legendItemStyle = (hidden) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: 4,
  opacity: hidden ? 0.35 : 1,
  background: 'none',
  border: 'none'
});

const legendSwatchStyle = (color, hidden) => ({
  width: 10,
  height: 10,
  borderRadius: 2,
  backgroundColor: hidden ? '#ccc' : color,
  flexShrink: 0
});

const legendLabelStyle = (hidden) => ({
  fontSize: 12,
  fontWeight: 500,
  color: hidden ? '#9aa1a9' : '#16191f',
  textDecoration: hidden ? 'line-through' : 'none'
});

const totalSwatchStyle = (hidden) => ({
  width: 16,
  height: 2,
  backgroundColor: hidden ? '#ccc' : CHART_COLORS.total,
  borderTop: `2px dashed ${hidden ? '#ccc' : CHART_COLORS.total}`,
  flexShrink: 0
});

const ACTION_LINK_STYLE = {
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: 12,
  fontWeight: 500,
  color: '#0073bb',
  background: 'none',
  border: 'none'
};

/**
 * Shared stacked-area chart used by the Available Labor and Required Labor
 * sections. Owns its own Expander, legend collapse, and clickable legend
 * state — callers just supply pre-built series and (optional) header extras.
 */
const StackedLaborChart = ({ title, series, totalData, netData, intervals, categories, headerExtras }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [showAllCohorts, setShowAllCohorts] = useState(false);
  const graphTheme = useVictoryTheme({ showIndependentGrid: false, interpolation: 'linear' });
  const { minutesMap, tickValues, tickFormat } = useMemo(() => buildTimeScale(categories), [categories]);
  const intervalLines = useMemo(() => buildIntervalLines(intervals, minutesMap), [intervals, minutesMap]);

  const graphData = useMemo(
    () =>
      series.map((s, idx) => ({
        key: s.key || `s-${idx}`,
        label: s.label,
        color: s.color || CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
        data: transformChartData(s.data, minutesMap)
      })),
    [series, minutesMap]
  );

  const visibleAreas = useMemo(() => graphData.filter((g) => !hiddenKeys.has(g.key)), [graphData, hiddenKeys]);
  const showTotal = !hiddenKeys.has(TOTAL_KEY);

  const totalLine = useMemo(() => transformChartData(totalData, minutesMap), [totalData, minutesMap]);

  const selectedLine = useMemo(() => {
    if (!visibleAreas.length) return [];
    const numPts = visibleAreas[0].data.length;
    return Array.from({ length: numPts }, (_, i) => ({
      x: visibleAreas[0].data[i].x,
      y: visibleAreas.reduce((sum, g) => sum + (g.data[i]?.y || 0), 0)
    }));
  }, [visibleAreas]);
  const hasSelected = selectedLine.length > 0 && selectedLine.some((d, i) => d.y !== totalLine[i]?.y);
  const showSelected = hasSelected && !hiddenKeys.has(NET_KEY);
  const allKeys = useMemo(
    () => [...graphData.map((g) => g.key), TOTAL_KEY, ...(hasSelected ? [NET_KEY] : [])],
    [graphData, hasSelected]
  );
  const isDefault = hiddenKeys.size === 0;

  const toggleKey = (key) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const yMax = useMemo(() => {
    const totalMax = showTotal && totalLine.length ? Math.max(...totalLine.map((d) => d.y)) : 0;
    if (visibleAreas.length > 0) {
      const numPts = visibleAreas[0].data.length;
      const stackMaxes = Array.from({ length: numPts }, (_, i) =>
        visibleAreas.reduce((sum, g) => sum + (g.data[i]?.y || 0), 0)
      );
      return Math.max(...stackMaxes, totalMax) * 1.15 || 100;
    }
    return totalMax ? totalMax * 1.15 : 100;
  }, [visibleAreas, showTotal, totalLine]);

  const xDomain = useMemo(() => {
    if (!tickValues.length) return undefined;
    return [Math.min(...tickValues), Math.max(...tickValues)];
  }, [tickValues]);

  if (!graphData.length) return null;

  // Total leads so it sits at the top of the hover tooltip, ahead of the cohorts.
  const legendData = [
    ...(showTotal ? [{ key: TOTAL_KEY, label: 'Total', color: CHART_COLORS.total, data: totalLine }] : []),
    ...visibleAreas.map(({ key, label, color, data }) => ({ key, label, color, data })),
    ...(showSelected ? [{ key: NET_KEY, label: 'Selected', color: CHART_COLORS.pick, data: selectedLine }] : [])
  ];

  return (
    <div style={{ width: '100%' }}>
      <Expander open={isExpanded} onChange={setIsExpanded} title={title} type="inline" spacingInset="300">
        <LegendProvider
          data={legendData}
          plugins={[victoryLinePlugin({ theme: graphTheme }), victoryTooltipPlugin({ showCrosshair: true })]}>
          {({ getLineProps, voronoiContainerProps }) => (
            <Column spacing="none">
              {headerExtras && (
                <Row alignmentHorizontal="end" spacingInset="none none 200 none">
                  {headerExtras}
                </Row>
              )}
              <VictoryChart
                theme={graphTheme}
                width={window.innerWidth * CHART_WIDTH_RATIO}
                height={380}
                padding={{ top: 40, left: 60, right: 0, bottom: 60 }}
                domain={{ x: xDomain, y: [0, yMax] }}
                containerComponent={
                  <VictoryVoronoiContainer
                    {...voronoiContainerProps}
                    labelComponent={formatTooltipTitle(voronoiContainerProps.labelComponent, tickFormat)}
                    labels={({ datum }) => (datum.y == null || isNaN(datum.y) ? '-' : `${(+datum.y).toFixed(1)} HC`)}
                  />
                }>
                {visibleAreas.length > 0 && (
                  <VictoryStack>
                    {visibleAreas.map(({ key, data, color }) => (
                      <VictoryArea
                        key={`area-${key}`}
                        data={data}
                        x="x"
                        y="y"
                        interpolation="stepAfter"
                        style={{ data: { fill: color, fillOpacity: 0.25, stroke: color, strokeWidth: 0 } }}
                      />
                    ))}
                  </VictoryStack>
                )}
                {showTotal && (
                  <VictoryLine
                    key={TOTAL_KEY}
                    data={totalLine}
                    x="x"
                    y="y"
                    interpolation="stepAfter"
                    style={{
                      ...getLineProps(TOTAL_KEY).style,
                      data: {
                        ...getLineProps(TOTAL_KEY).style?.data,
                        strokeDasharray: '6,3',
                        strokeWidth: 2.5
                      }
                    }}
                  />
                )}
                {showSelected && (
                  <VictoryLine
                    key={NET_KEY}
                    data={selectedLine}
                    x="x"
                    y="y"
                    interpolation="stepAfter"
                    style={{
                      ...getLineProps(NET_KEY).style,
                      data: {
                        ...getLineProps(NET_KEY).style?.data,
                        strokeDasharray: '4,4',
                        strokeWidth: 2
                      }
                    }}
                  />
                )}
                {visibleAreas.map(({ key, data }) => (
                  <VictoryLine
                    key={key}
                    data={data}
                    x="x"
                    y="y"
                    interpolation="stepAfter"
                    style={{
                      ...getLineProps(key).style,
                      data: { ...getLineProps(key).style?.data, stroke: 'transparent', strokeWidth: 0 }
                    }}
                  />
                ))}
                <VictoryAxis
                  tickValues={tickValues}
                  tickFormat={tickFormat}
                  style={{
                    tickLabels: { fontSize: 10, fill: '#5a6470', angle: 45, padding: 15 },
                    axisLabel: { fontSize: 11, fill: '#5a6470', padding: 45 }
                  }}
                  label="Time"
                  fixLabelOverlap
                />
                <VictoryAxis
                  dependentAxis
                  style={{
                    tickLabels: { fontSize: 10, fill: '#5a6470' },
                    axisLabel: { fontSize: 11, fill: '#5a6470', padding: 45 }
                  }}
                  label="Head Count"
                />
                {renderIntervalLines(intervalLines, yMax)}
              </VictoryChart>
              {/* Clickable legend */}
              <Row spacing="300" wrap="down" spacingInset="200 none none none" alignmentHorizontal="center">
                {(showAllCohorts ? graphData : graphData.slice(0, COHORT_LEGEND_VISIBLE_THRESHOLD)).map(
                  ({ key, label, color }) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => toggleKey(key)}
                      style={legendItemStyle(hiddenKeys.has(key))}>
                      <span style={legendSwatchStyle(color, hiddenKeys.has(key))} />
                      <span style={legendLabelStyle(hiddenKeys.has(key))}>{label}</span>
                    </button>
                  )
                )}
                {graphData.length > COHORT_LEGEND_VISIBLE_THRESHOLD && (
                  <button
                    type="button"
                    onClick={() => setShowAllCohorts((v) => !v)}
                    style={{ ...ACTION_LINK_STYLE, background: 'none', border: 'none' }}>
                    {showAllCohorts
                      ? 'Show fewer'
                      : `Show all (${graphData.length - COHORT_LEGEND_VISIBLE_THRESHOLD} more)`}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleKey(TOTAL_KEY)}
                  style={legendItemStyle(hiddenKeys.has(TOTAL_KEY))}>
                  <span style={totalSwatchStyle(hiddenKeys.has(TOTAL_KEY))} />
                  <span style={{ ...legendLabelStyle(hiddenKeys.has(TOTAL_KEY)), fontWeight: 500 }}>Total</span>
                </button>
                {hasSelected && (
                  <button
                    type="button"
                    onClick={() => toggleKey(NET_KEY)}
                    style={legendItemStyle(hiddenKeys.has(NET_KEY))}>
                    <span
                      style={{
                        ...totalSwatchStyle(hiddenKeys.has(NET_KEY)),
                        backgroundColor: hiddenKeys.has(NET_KEY) ? '#ccc' : CHART_COLORS.pick,
                        borderTopColor: hiddenKeys.has(NET_KEY) ? '#ccc' : CHART_COLORS.pick
                      }}
                    />
                    <span style={{ ...legendLabelStyle(hiddenKeys.has(NET_KEY)), fontWeight: 500 }}>Selected</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => (isDefault ? setHiddenKeys(new Set(allKeys)) : setHiddenKeys(new Set()))}
                  style={{ ...ACTION_LINK_STYLE, background: 'none', border: 'none' }}>
                  {isDefault ? 'Deselect All' : 'Reset'}
                </button>
              </Row>
            </Column>
          )}
        </LegendProvider>
      </Expander>
    </div>
  );
};

export default StackedLaborChart;

/**
 * Clip an HH:MM-keyed series to the contiguous range [startLabel, endLabel]
 * within `orderedCategories`. The categories array is already in chronological
 * order (built from sorted ISOs), so a positional slice handles cross-midnight
 * windows without ambiguity.
 */
export const clipSeriesToRange = (data, orderedCategories, startLabel, endLabel) => {
  if (!data?.length || !orderedCategories?.length) return data || [];
  const startIdx = orderedCategories.indexOf(startLabel);
  const endIdx = orderedCategories.indexOf(endLabel);
  if (startIdx < 0 || endIdx < 0) return data;
  const allowed = new Set(orderedCategories.slice(startIdx, endIdx + 1));
  return data.filter((pt) => allowed.has(pt.x));
};

export const clipCategoriesToRange = (orderedCategories, startLabel, endLabel) => {
  if (!orderedCategories?.length) return [];
  const startIdx = orderedCategories.indexOf(startLabel);
  const endIdx = orderedCategories.indexOf(endLabel);
  if (startIdx < 0 || endIdx < 0) return orderedCategories;
  return orderedCategories.slice(startIdx, endIdx + 1);
};
